import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from "../services/firebase";
import Logger from "../utils/logger";

type UserRole = "admin" | null;

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  role: UserRole;
  loginError: string | null;
  clearLoginError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<UserRole>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);

      if (user) {
        // カスタムクレームでロールをチェック
        const tokenResult = await user.getIdTokenResult();
        const userRole = tokenResult.claims.role as UserRole;
        setRole(userRole || null);
      } else {
        setRole(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    setLoginError(null); // エラーをクリア
    Logger.info("🔐 Login attempt:", { email, passwordLength: password.length });
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      Logger.info("✅ Login successful:", userCredential.user.uid);
      Logger.setUser({
        id: userCredential.user.uid,
        email: userCredential.user.email || undefined,
      });
    } catch (error) {
      Logger.error("❌ Login failed:", error);
      setLoading(false);
      const errorCode =
        error && typeof error === "object" && "code" in error
          ? (error as { code: string }).code
          : "unknown";
      setLoginError(errorCode);
      throw error;
    }
  };

  const clearLoginError = () => {
    setLoginError(null);
  };

  const logout = async () => {
    Logger.clearUser();
    await signOut(auth);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAdmin: role === "admin",
    role,
    loginError,
    clearLoginError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
